// DnDCharacterSheet.tsx - Updated to use database
import React, { useState } from "react";
import {
	Sword,
	Shield,
	Heart,
	Brain,
	Sparkles,
	Backpack,
	ScrollText,
	Users,
	Save,
	AlertCircle,
	CheckCircle,
} from "lucide-react";

import { TabId } from "./types";
import { TabButton } from "./TabButton";
import { CoreTab } from "./CoreTab";
import { CombatTab } from "./CombatTab";
import { SkillsTab } from "./SkillsTab";
import { SpellsTab } from "./SpellsTab";
import { InventoryTab } from "./InventoryTab";
import { FeaturesTab } from "./FeaturesTab";
import { BackstoryTab } from "./BackstoryTab";
import { CharacterManager } from "./CharacterManager";
import { useCharacter } from "./useCharacter";

const DnDCharacterSheet: React.FC = () => {
	const [activeTab, setActiveTab] = useState<TabId>("core");
	const [lastSaved, setLastSaved] = useState<Date | null>(null);
	const [saveStatus, setSaveStatus] = useState<
		"idle" | "saving" | "saved" | "error"
	>("idle");

	const {
		character,
		currentCharacterId,
		characters,
		isLoading,
		error,
		updateCharacter,
		addItem,
		removeItem,
		loadCharacter,
		saveCharacter,
		createNewCharacter,
		deleteCharacter,
		exportCharacter,
		importCharacter,
		clearError,
	} = useCharacter();

	const handleSaveCharacter = async () => {
		try {
			setSaveStatus("saving");
			await saveCharacter();
			setLastSaved(new Date());
			setSaveStatus("saved");
			setTimeout(() => setSaveStatus("idle"), 3000);
			return currentCharacterId || 0;
		} catch (error) {
			setSaveStatus("error");
			setTimeout(() => setSaveStatus("idle"), 3000);
			throw error;
		}
	};

	const handleLoadCharacter = async (id: number) => {
		try {
			await loadCharacter(id);
		} catch (error) {
			console.error("Failed to load character:", error);
		}
	};

	const handleDeleteCharacter = async (id: number) => {
		try {
			await deleteCharacter(id);
		} catch (error) {
			console.error("Failed to delete character:", error);
		}
	};

	const renderTabContent = () => {
		switch (activeTab) {
			case "core":
				return (
					<CoreTab
						character={character}
						updateCharacter={updateCharacter}
					/>
				);
			case "combat":
				return (
					<CombatTab
						character={character}
						updateCharacter={updateCharacter}
						addItem={addItem}
						removeItem={removeItem}
					/>
				);
			case "skills":
				return (
					<SkillsTab
						character={character}
						updateCharacter={updateCharacter}
					/>
				);
			case "spells":
				return (
					<SpellsTab
						character={character}
						updateCharacter={updateCharacter}
					/>
				);
			case "inventory":
				return (
					<InventoryTab
						character={character}
						updateCharacter={updateCharacter}
						addItem={addItem}
						removeItem={removeItem}
					/>
				);
			case "features":
				return (
					<FeaturesTab
						character={character}
						updateCharacter={updateCharacter}
						addItem={addItem}
						removeItem={removeItem}
					/>
				);
			case "backstory":
				return (
					<BackstoryTab
						character={character}
						updateCharacter={updateCharacter}
					/>
				);
			default:
				return null;
		}
	};

	// Show loading screen while database initializes
	if (isLoading && characters.length === 0) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
					<h2 className="text-xl font-semibold text-gray-700 mb-2">
						Loading Character Sheet
					</h2>
					<p className="text-gray-500">Initializing database...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
			{/* Character Manager */}
			<CharacterManager
				characters={characters}
				currentCharacterId={currentCharacterId}
				onLoadCharacter={handleLoadCharacter}
				onDeleteCharacter={handleDeleteCharacter}
				onCreateNew={createNewCharacter}
				onSaveCharacter={handleSaveCharacter}
				onExport={exportCharacter}
				onImport={importCharacter}
				isLoading={isLoading}
				currentCharacterName={character.name}
			/>

			<div className="max-w-7xl mx-auto p-6">
				<div className="text-center mb-10">
					<h1 className="text-5xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
						D&D 5e Character Sheet
					</h1>
					<p className="text-gray-600 text-lg mb-6">
						{character.name
							? `Editing: ${character.name}`
							: "Create and manage your adventurer"}
					</p>

					{/* Status and Save Controls */}
					<div className="flex flex-wrap gap-4 justify-center items-center">
						<button
							onClick={handleSaveCharacter}
							disabled={saveStatus === "saving"}
							className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-200 ${
								saveStatus === "saving"
									? "bg-gray-400 text-white cursor-not-allowed"
									: saveStatus === "saved"
									? "bg-green-500 text-white"
									: saveStatus === "error"
									? "bg-red-500 text-white"
									: "bg-blue-500 text-white hover:bg-blue-600"
							}`}
						>
							{saveStatus === "saving" ? (
								<>
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
									Saving...
								</>
							) : saveStatus === "saved" ? (
								<>
									<CheckCircle size={20} />
									Saved!
								</>
							) : saveStatus === "error" ? (
								<>
									<AlertCircle size={20} />
									Error
								</>
							) : (
								<>
									<Save size={20} />
									Save Character
								</>
							)}
						</button>

						{lastSaved && (
							<div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl text-sm text-gray-600">
								<CheckCircle size={16} />
								Last saved: {lastSaved.toLocaleTimeString()}
							</div>
						)}

						{currentCharacterId && (
							<div className="flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-xl text-sm text-indigo-800">
								<Users size={16} />
								Character ID: {currentCharacterId}
							</div>
						)}
					</div>

					{/* Error Display */}
					{error && (
						<div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700 max-w-md mx-auto">
							<div className="flex items-center gap-2 mb-2">
								<AlertCircle size={16} />
								<span className="font-semibold">Error</span>
							</div>
							<p className="text-sm">{error}</p>
							<button
								onClick={clearError}
								className="mt-2 px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors duration-200"
							>
								Dismiss
							</button>
						</div>
					)}
				</div>

				{/* Tab Navigation */}
				<div className="flex flex-wrap gap-3 mb-8 justify-center">
					<TabButton
						id="core"
						icon={Brain}
						label="Core"
						active={activeTab === "core"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
					<TabButton
						id="combat"
						icon={Sword}
						label="Combat"
						active={activeTab === "combat"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
					<TabButton
						id="skills"
						icon={Heart}
						label="Skills"
						active={activeTab === "skills"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
					<TabButton
						id="spells"
						icon={Sparkles}
						label="Spells"
						active={activeTab === "spells"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
					<TabButton
						id="inventory"
						icon={Backpack}
						label="Inventory"
						active={activeTab === "inventory"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
					<TabButton
						id="features"
						icon={ScrollText}
						label="Features"
						active={activeTab === "features"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
					<TabButton
						id="backstory"
						icon={Users}
						label="Backstory"
						active={activeTab === "backstory"}
						onClick={(e) => {
							setActiveTab(e as TabId);
						}}
					/>
				</div>

				{/* Tab Content */}
				<div className="pb-10">{renderTabContent()}</div>
			</div>
		</div>
	);
};

export default DnDCharacterSheet;
